import type { Subjects } from '@/shared/lib/casl'
import {
  LayoutDashboard,
  Users,
  Droplets,
  Microscope,
  Package,
  BarChart3,
  ClipboardList,
  Settings,
  Library,
  SquareActivity,
  Recycle,
  HeartPulse,
  Truck,
} from 'lucide-react'

export interface INavData {
  key: Exclude<Subjects, 'all'>
  /** Translation key under `nav.` — defaults to `key` when the label differs from the CASL subject. */
  labelKey?: string
  path?: string
  /** Search params the link carries; also used to tell same-path tab items apart. */
  search?: Record<string, string>
  onClick?: () => void
  icon?: React.ElementType
  children?: INavData[]
  hide?: boolean
}

export function flattenNavData(items: INavData[]): INavData[] {
  const out: INavData[] = []
  for (const item of items) {
    out.push(item)
    if (item.children?.length) {
      out.push(...flattenNavData(item.children))
    }
  }
  return out
}

export const getNavData = (): INavData[] => {
  return [
    {
      // Registered only so the route guard can resolve `/home`; never shown.
      key: 'home',
      path: '/home',
      hide: true,
    },
    {
      icon: LayoutDashboard,
      key: 'dashboard',
      path: '/dashboard',
    },
    {
      icon: Users,
      key: 'donors',
      path: '/donors',
      children: [
        {
          key: 'donors',
          labelKey: 'donors-admitted',
          path: '/donors',
          search: { tab: 'admitted' },
        },
        {
          key: 'donors',
          labelKey: 'donors-deferred',
          path: '/donors',
          search: { tab: 'deferred' },
        },
        {
          key: 'specialized-donors',
          path: '/specialized-donors',
        },
      ],
    },
    {
      icon: SquareActivity,
      key: 'labarotaroyResearch',
      path: '/labarotaroyResearch',
      children: [
        {
          key: 'labarotaroyResearch',
          labelKey: 'labarotaroyResearch-screening',
          path: '/labarotaroyResearch',
          search: { tab: 'screening' },
        },
        {
          key: 'labarotaroyResearch',
          labelKey: 'labarotaroyResearch-collection',
          path: '/labarotaroyResearch',
          search: { tab: 'blood-collection' },
        },
      ],
    },
    {
      icon: Microscope,
      key: 'laboratory',
      path: '/laboratory',
      children: [
        {
          key: 'laboratory',
          labelKey: 'laboratory-analyses',
          path: '/laboratory',
          search: { tab: 'analyses' },
        },
        {
          key: 'laboratory',
          labelKey: 'laboratory-pcr',
          path: '/laboratory',
          search: { tab: 'pcr' },
        },
        {
          key: 'laboratory',
          labelKey: 'laboratory-emergency-notifications',
          path: '/laboratory',
          search: { tab: 'emergency-notifications' },
        },
      ],
    },
    { icon: Droplets, key: 'donations', path: '/donations' },
    { icon: Recycle, key: 'processing', path: '/processing' },
    {
      icon: Truck,
      key: 'expedition',
      children: [
        {
          key: 'expedition',
          labelKey: 'expedition-storage',
          path: '/expedition/storage',
        },
        {
          key: 'expedition',
          labelKey: 'expedition-fractionation',
          path: '/expedition/fractionation',
        },
        {
          key: 'expedition',
          labelKey: 'expedition-cryoprecipitate',
          path: '/expedition/cryoprecipitate',
        },
        {
          key: 'expedition',
          labelKey: 'expedition-secondary-processing',
          path: '/expedition/secondary-processing',
        },
        {
          key: 'expedition',
          labelKey: 'expedition-quality-control',
          path: '/expedition/quality-control',
        },
      ],
    },
    {
      icon: Library,
      key: 'dictionary',
      children: [
        {
          key: 'dictionary-analysis',
          path: '/dictionary/analysis',
        },
        {
          key: 'dictionary-organizations',
          path: '/dictionary/organizations',
        },
      ],
    },

    {
      icon: Package,
      key: 'warehouse',
      path: '/warehouse',
      children: [
        {
          key: 'warehouse-containers',
          path: '/warehouse/containers',
        },
        {
          key: 'warehouse-applications',
          path: '/warehouse/applications',
        },
        {
          key: 'warehouse-issuance',
          path: '/warehouse/issuance',
        },
        {
          key: 'warehouse-stock',
          path: '/warehouse/stock',
        },
      ],
    },
    {
      icon: HeartPulse,
      key: 'recipients-section',
      children: [
        {
          key: 'recipients',
          labelKey: 'recipients-list',
          path: '/recipients/list',
        },
        {
          key: 'transfusions',
          path: '/recipients/transfusions',
        },
        {
          key: 'blood-consumption',
          path: '/recipients/blood-consumption',
        },
      ],
    },
    {
      icon: ClipboardList,
      key: 'contracts',
      path: '/contracts',
    },
    {
      icon: BarChart3,
      key: 'reports',
      path: '/reports',
    },
    { icon: Settings, key: 'settings', path: '/settings' },
  ]
}
